import { createUseStyles } from "react-jss";
import colors from "@/constants/colors";
import {
  defaultDataTableTheme,
  PINNED_SHADOWS,
  TABLE_Z_INDEX,
} from "./DataTable.theme";

/**
 * Shared styles for data tables across the application.
 * Use these styles to ensure visual consistency between all list views.
 */
export const useDataTableStyles = createUseStyles({
  // === Container ===
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    minWidth: 0, // Prevents flex child from overflowing parent
    overflow: "hidden", // Contains table within bounds
  },

  // === Header area ===
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1rem",
    gap: "1rem",
    flexWrap: "wrap",
  },
  headerActions: {
    display: "flex",
    gap: "1rem",
    "& $pulseActive": {
      animation: "$orangePulse 1.5s infinite",
      backgroundColor: "var(--mela-color-warning-solid-bg) !important",
      color: "var(--mela-color-warning-solid-text) !important",
      borderColor: "var(--mela-color-warning-solid-bg) !important",
    },
  },
  pulseActive: {},
  "@keyframes orangePulse": {
    "0%": {
      boxShadow: "0 0 0 0 var(--mela-color-warning-focus-ring)",
      transform: "scale(1)",
    },
    "70%": {
      boxShadow: "0 0 0 10px transparent",
      transform: "scale(1.02)",
    },
    "100%": {
      boxShadow: "0 0 0 0 transparent",
      transform: "scale(1)",
    },
  },
  filterContainer: {
    display: "flex",
    gap: "1rem",
    alignItems: "flex-start",
    marginBottom: "1rem",
    minWidth: 0,
    width: "100%",
  },
  toolbarContent: {
    display: "flex",
    alignItems: "center",
    flex: "1 1 auto",
    flexWrap: "wrap",
    gap: "0.5rem",
    minWidth: 0,
  },
  toolbarActions: {
    alignItems: "center",
    display: "flex",
    flex: "0 0 auto",
    gap: 8,
    marginLeft: "auto",
  },

  // === Search ===
  searchContainer: {
    display: "flex",
    gap: "1rem",
    alignItems: "center",
  },
  searchInput: {
    minWidth: 120,
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "var(--mela-surface-card)",
    },
  },

  // === Table ===
  tableContainer: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    border: "1px solid var(--mela-border-default)",
    borderRadius: "8px",
    backgroundColor: "var(--mela-surface-card)",
    boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
    overflowX: "auto",
    overflowY: "hidden",
    boxSizing: "border-box",
    "& table": {
      tableLayout: "fixed",
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
    },
    "& thead": {
      position: "sticky",
      top: 0,
      zIndex: TABLE_Z_INDEX.stickyHeaderFooter,
    },
    "& tbody": {
      backgroundColor: "var(--mela-surface-card)",
    },
    "& tfoot": {
      position: "sticky",
      bottom: 0,
      zIndex: TABLE_Z_INDEX.stickyHeaderFooter,
    },
  },

  tableHead: {
    backgroundColor: "var(--mela-surface-card)",
    "& tr": {
      backgroundColor: "var(--mela-surface-card)",
    },
  },
  tableHeaderCell: {
    fontWeight: 600,
    color: "var(--mela-text-secondary)",
    fontSize: "0.75rem",
    borderBottom: "1px solid var(--mela-border-default)",
    textTransform: "uppercase",
    backgroundColor: "var(--mela-surface-card)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    transition: "box-shadow 0.3s ease",
  },
  tableRow: {
    backgroundColor: defaultDataTableTheme.rowBg,
    position: "relative",
    // Apply hover background to ALL cells in the row
    "&:hover": {
      backgroundColor: defaultDataTableTheme.rowHoverBg,
      "& td": {
        backgroundColor: `${defaultDataTableTheme.rowHoverBg} !important`,
      },
    },
    "&.Mui-selected": {
      backgroundColor: defaultDataTableTheme.rowSelectedBg,
      "& td": {
        backgroundColor: `${defaultDataTableTheme.rowSelectedBg} !important`,
      },
      "&:hover": {
        backgroundColor: defaultDataTableTheme.rowSelectedHoverBg,
        "& td": {
          backgroundColor: `${defaultDataTableTheme.rowSelectedHoverBg} !important`,
        },
      },
    },
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  },
  highlightedRow: {
    backgroundColor: defaultDataTableTheme.rowSelectedBg,
    "& td": {
      backgroundColor: `${defaultDataTableTheme.rowSelectedBg} !important`,
    },
    "&:hover": {
      backgroundColor: defaultDataTableTheme.rowSelectedHoverBg,
      "& td": {
        backgroundColor: `${defaultDataTableTheme.rowSelectedHoverBg} !important`,
      },
    },
  },
  archivedRow: {
    opacity: 0.6,
    "& td": {
      pointerEvents: "auto",
    },
    "& td > *": {
      pointerEvents: "none",
    },
    "& .MuiCheckbox-root": {
      pointerEvents: "auto",
    },
    "& .MuiIconButton-root": {
      pointerEvents: "auto",
    },
    "& $archivedIndicator": {
      pointerEvents: "auto",
    },
  },
  archivedIndicator: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 4,
    width: 4,
    height: "100%",
    backgroundColor: colors.ashGrey,
    cursor: "help",
    zIndex: TABLE_Z_INDEX.pinnedBody + 1,
    pointerEvents: "auto",
  },
  tableCell: {
    borderBottom: "none",
    color: "var(--mela-text-secondary)",
    fontSize: "0.875rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    backgroundColor: "inherit",
    transition: "box-shadow 0.3s ease",
  },
  tableCellEllipsis: {
    textOverflow: "ellipsis",
  },
  tableCellCentered: {
    borderBottom: "1px solid var(--mela-border-default)",
    textAlign: "center",
  },

  // === Table Body ===
  tableBody: {
    // Hide border overlap: no top border on first row, no bottom border on last row
    "& tr:first-child td": {
      borderTop: "none",
    },
    "& tr:last-child td": {
      borderBottom: "none",
    },
  },

  // === Column Dividers ===
  columnDivider: {
    borderLeft: "1px solid var(--mela-border-default)",
  },

  // === Table Footer (pagination) ===
  tableFooter: {
    backgroundColor: "transparent",
  },
  tableFooterCell: {
    padding: "0.75rem 1rem",
    borderTop: "1px solid var(--mela-border-default)",
    backgroundColor: "var(--mela-surface-card)",
  },
  paginationContainer: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "0.5rem 1rem",
  },
  rowsPerPageContainer: {
    display: "flex",
    alignItems: "center",
    gap: "0.375rem",
    color: "var(--mela-text-secondary)",
  },
  paginationLabel: {
    fontSize: "0.8125rem",
    lineHeight: 1.1,
    whiteSpace: "nowrap",
  },
  paginationSelect: {
    minWidth: "50px",
    fontSize: "0.8125rem",
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      minHeight: "unset",
      padding: "4px 22px 4px 8px",
      lineHeight: 1,
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1rem",
      right: "6px",
    },
  },
  navContainer: {
    display: "flex",
    alignItems: "center",
    gap: "0.25rem",
  },
  pageIndicator: {
    fontSize: "0.875rem",
    color: "var(--mela-text-secondary)",
    minWidth: 80,
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    gap: "2px",
  },
  inlinePageInput: {
    border: "1px solid var(--mela-border-subtle)",
    borderRadius: "4px",
    background: "var(--mela-surface-card)",
    fontSize: "0.875rem",
    color: "var(--mela-text-secondary)",
    fontFamily: "inherit",
    textAlign: "center",
    padding: "2px 6px",
    outline: "none",
    cursor: "text",
    "&:focus": {
      borderColor: "var(--mela-color-primary)",
      boxShadow: "0 0 0 2px var(--mela-color-primary-soft-bg)",
    },
  },
  pageInputContainer: {
    display: "flex",
    alignItems: "center",
    gap: "0.25rem",
    marginLeft: "0.5rem",
  },
  pageInput: {
    width: "60px",
    "& .MuiInputBase-input": {
      padding: "4px 8px",
      fontSize: "0.875rem",
      textAlign: "center",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "4px",
    },
  },
  applyButton: {
    padding: 4,
    color: "var(--mela-color-success-solid-bg)",
    "&:disabled": {
      opacity: 0.3,
    },
  },
  navButton: {
    padding: 6,
    "&:disabled": {
      opacity: 0.3,
    },
  },

  // === Actions ===
  actionsCell: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "0.5rem",
  },
  actionButton: {
    padding: "6px 14px",
    height: 36,
    display: "flex",
    alignItems: "center",
  },

  // === Chips ===
  chip: {
    height: 24,
    fontSize: "0.75rem",
  },

  // === Empty / Loading states ===
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "3rem",
    color: "var(--mela-text-muted)",
  },
  // === Selection Banner ===
  selectionBanner: {
    backgroundColor: "var(--mela-color-primary-soft-bg)",
    padding: "8px 16px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "0.875rem",
    borderBottom: "1px solid var(--mela-border-default)",
    gap: "8px",
  },
  selectionLink: {
    color: "var(--mela-color-primary)",
    cursor: "pointer",
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  // === Shadows ===
  pinnedLeftShadow: {
    boxShadow: `${PINNED_SHADOWS.left} !important`,
  },
  pinnedRightShadow: {
    boxShadow: `${PINNED_SHADOWS.right} !important`,
  },

  // === Pinned States Fix ===
  // This ensures that pinned cells keep their solid background on hover/selection
  pinnedCell: {
    backgroundColor: "inherit",
    "tr:hover &": {
      backgroundColor: `${defaultDataTableTheme.rowHoverBg} !important`,
    },
    "tr.Mui-selected &": {
      backgroundColor: `${defaultDataTableTheme.rowSelectedBg} !important`,
      "tr:hover &": {
        backgroundColor: `${defaultDataTableTheme.rowSelectedHoverBg} !important`,
      },
    },
    "tr$highlightedRow &": {
      backgroundColor: `${defaultDataTableTheme.rowSelectedBg} !important`,
    },
    "tr$highlightedRow:hover &": {
      backgroundColor: `${defaultDataTableTheme.rowSelectedHoverBg} !important`,
    },
  },
});

export default useDataTableStyles;
